<script lang="ts">
	import Header from '#blocks/header.svelte'
	import Hero from '#blocks/hero.svelte'
	import Navblock from '#blocks/navblock.svelte'
	import Render from '#components/render.svelte'
	import Photousel from '#blocks/photousel.svelte'
	import Sidekick from '#blocks/sidekick.svelte'
	import Actions from '#blocks/actions.svelte'
	import LinkList from '#blocks/linklist.svelte'
	import { Storyblok } from '#library/storyblok.svelte.ts'
	import Accordion from '#blocks/accordion.svelte'
	import type { ComponentProps } from 'svelte'
	import type { Subject } from '#library/types.ts'
	import * as _ from 'radashi'
	import { page } from '$app/state'
	import { SvelteSet } from 'svelte/reactivity'
	import { gen_id } from '#library/id.ts'
	import { scrollto } from '#library/scrollto.ts'

	interface Props {
		data: Storyblok<Subject>
	}
	let { data }: Props = $props()

	const content = $derived(data.story?.content)

	const first = $derived(content?.tabs?.[0]?.title ? ref(content.tabs[0]) : null)

	// Set active to either the hash or the first item's title
	let active_tab: string | null = $derived.by(() => {
		const all_tab_refs = new SvelteSet(content?.tabs?.map(ref) || [])
		const hash = page.url.hash.slice(1).split('/')[0]
		if (all_tab_refs.has(hash)) return hash
		return first
	})

	function ref(item: NonNullable<Subject['content']['tabs']>[number]): string {
		return gen_id(item.title)!
	}

	function set_active_tab(id: string) {
		active_tab = id
		scrollto(id)
	}
</script>

<Header />

{#if content}
	{#if content.image}
		<Hero
			blok={{
				theme: 'theme-crocus-on-light-gimson',
				fill: true,
				anchor: 'intro',
				image: content.image
			} as unknown as ComponentProps<typeof Hero>['blok']}
		/>
	{/if}

	{#if content.intro_title}
		<Navblock
			blok={{
				theme: 'theme-lupton-on-gimson',
				title: {
					text: content.intro_title,
					tag: 'h1'
				},
				body: content.intro_body
			} as unknown as ComponentProps<typeof Navblock>['blok']}
		/>
	{/if}

	{#if content.tabs?.length}
		<section class="tabs">
			<div class="contain">
				{#if content.tabs?.length > 1}
					<div class="header">
						{#each content.tabs as tab (tab._uid)}
							{@const id = ref(tab)}
							<button class="title" class:active={active_tab === id} onclick={() => set_active_tab(id)}>
								{tab.title}
							</button>
						{/each}
					</div>
				{/if}
				<div class="main">
					{#each content.tabs as tab (tab._uid)}
						{@const id = ref(tab)}
						{#if active_tab === id}
							<div class="tab" {id}>
								{#if tab.body?.content?.[0].content}
									<div class="intro">
										<div>
											<div class="body">
												<Render content={tab.body} />
											</div>
										</div>
									</div>
								{/if}
								{#each tab.blocks ?? [] as blok (blok._uid)}
									{#if blok.component === 'accordion'}
										<Accordion
											blok={{
												...blok,
												anchor: blok.anchor || gen_id(tab.title!)
											}}
										/>
									{:else}
										<Storyblok.Block {blok} />
									{/if}
								{/each}
							</div>
						{/if}
					{/each}
				</div>
			</div>
		</section>
	{/if}

	{#if content.faculty_title}
		<Sidekick
			blok={{
				theme: 'theme-crocus-on-light-gimson',
				title: {
					text: content.faculty_title
				},
				body: content.faculty_body,
				padding_bottom: '104',
				padding_top: '104'
			} as never}
		/>
		{#if content.faculty_gallery?.length}
			<Photousel
				blok={{
					theme: 'theme-crocus-on-light-gimson',
					items: content.faculty_gallery,
					padding_top: '0'
				} as unknown as ComponentProps<typeof Photousel>['blok']}
			/>
		{/if}
	{/if}

	<Actions
		blok={{
			theme: 'theme-pink-on-lupton',
			alt: true,
			title: 'Browse our prospectuses',
			subtitle:
				'See our detailed prospectuses to understand our innovative curriculum, vibrant school life, and supportive community.',
			items: [
				{
					link: {
						text: 'Get the prospectus',
						url: '/admissions/request-prospectus'
					}
				}
			]
		} as unknown as ComponentProps<typeof Actions>['blok']}
	/>

	<LinkList
		blok={{
			theme: 'theme-rose-on-white',
			title: 'Discover what we stand for',
			body: 'Bedales champions an innovative student-centred education, prioritising academic freedom, creativity, sustainability and wellbeing. We foster individuality, moral purpose and a lifelong love of learning in a changing world.',
			items: [
				{
					link: {
						text: 'Learning',
						url: '/learning'
					}
				},
				{
					link: {
						text: 'Living',
						url: '/living'
					}
				},
				{
					link: {
						text: 'Community',
						url: '/community'
					}
				},
				{
					link: {
						text: 'Environment',
						url: '/environment'
					}
				}
			]
		} as unknown as ComponentProps<typeof LinkList>['blok']}
	/>
{/if}

<style lang="css">
	.tabs {
		background: var(--c-white);
		color: var(--c-foreground);
		padding-block: var(--s-104);
	}

	.tab {
		scroll-margin-block-start: 20rem;
	}

	.header {
		display: flex;
		gap: 2rem;
		padding-block: 0.5rem calc(var(--s-40) + 0.5rem);
		white-space: nowrap;
		overflow-x: auto;
		margin-inline: -2rem;
		padding-inline: 2rem;
	}

	.title {
		font: 600 var(--f-h3);
		letter-spacing: var(--ls-h3);

		&.active {
			color: var(--c-rose);
			text-decoration: underline;
		}
	}

	.intro {
		display: grid;
		padding-block-end: var(--s-40);

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
			gap: 2rem;
		}
	}

	.body {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);

		:global(.button) {
			margin-block-start: 2rem;
		}
	}
</style>
